import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Footer } from '../components/Footer';
import { usage, sections as sectionsApi, UsageSummary, Section } from '../lib/api';
import { useAuth } from '../lib/auth';
import { STEM_META, STEM_ORDER, StemName, fmtTime, fmtRelativeDate } from '../lib/format';
import { STEM_COLOR, STEM_UI } from '../components/studioIcons';
import { setPendingUpload } from '../lib/pendingUpload';
import { listSeparations } from '../lib/stemCache';
import './dashboard.css';

const CHROME_STORE_URL =
  'https://chromewebstore.google.com/detail/riffloop-ai-stem-splitter/meinmcdiijnhaljldocknpglaehbmbmd';

// ── helpers ──
function hashSeed(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
function makeBars(seed: number, n: number): number[] {
  const out: number[] = [];
  let x = (seed || 1) >>> 0;
  for (let i = 0; i < n; i++) {
    x = (x * 9301 + 49297) % 233280;
    out.push(16 + Math.round((x / 233280) * 80));
  }
  return out;
}
function songName(s: Section): string {
  const v = s.video_id || '';
  if (s.source_kind === 'url' || /^https?:\/\//i.test(v)) return 'YouTube clip';
  const base = v.replace(/\.[a-z0-9]{1,5}$/i, '').replace(/_+/g, ' ').trim();
  return base || s.label || 'Untitled';
}
// A track's real name: prefer the captured source title (the actual song),
// falling back to the derived name.
function trackTitle(s: Section): string {
  if (s.title && s.title.trim()) return s.title.trim();
  return songName(s);
}
// The YouTube video id behind a section's video_id (bare id or a full URL) —
// used to fetch the thumbnail. null for uploads / unresolvable ids.
function ytIdOf(videoId: string): string | null {
  if (/^[\w-]{6,15}$/.test(videoId)) return videoId;
  const m = (videoId || '').match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([\w-]{6,})/);
  return m ? m[1] : null;
}
function mixLabel(s: Section): string {
  const audible = STEM_ORDER.filter((st) => (s.stem_mix?.[st] ?? (st === (s.stem as StemName) ? 1 : 0)) > 0.02);
  if (audible.length <= 1) return STEM_META[audible[0] ?? s.stem]?.label ?? s.stem;
  if (!audible.includes('vocals') && audible.length >= 2) return 'Instrumental';
  return 'Mix';
}
// Loudest >0.02 stem of a section's mix — the ACTUALLY-audible stem, never
// s.stem alone (a Vocals pick that auto-swapped reads stem='vocals' while the
// audible content is Solo & Lead).
const audibleStem = (s: Section): StemName => {
  const a = STEM_ORDER
    .filter((st) => (s.stem_mix?.[st] ?? (st === (s.stem as StemName) ? 1 : 0)) > 0.02)
    .sort((x, y) => (s.stem_mix?.[y] ?? 0) - (s.stem_mix?.[x] ?? 0));
  return (a[0] as StemName) ?? (s.stem as StemName);
};
const colorOf = (s: Section) => STEM_COLOR[audibleStem(s)] ?? 'var(--dash-accent)';
const tintOf = (c: string) => `color-mix(in srgb, ${c} 14%, transparent)`;

function Wave({ seed, color, n = 40, height = 32 }: { seed: number; color: string; n?: number; height?: number }) {
  const bars = makeBars(seed, n);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 2, height, width: '100%' }}>
      {bars.map((h, i) => (
        <span
          key={i}
          style={{
            flex: '1 1 0',
            minWidth: 2,
            maxWidth: 4,
            height: `${h}%`,
            borderRadius: 3,
            background: color,
            opacity: 0.34 + (h / 100) * 0.42,
          }}
        />
      ))}
    </div>
  );
}

export function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [u, setU] = useState<UsageSummary | null>(null);
  const [all, setAll] = useState<Section[] | null>(null);
  // Sections come from the server, but the audio they resume lives only in this
  // device's IndexedDB. Without this the cards happily offered an upload whose
  // stems were evicted (or that was separated on another device) and the Studio
  // could only answer "that track's audio isn't on this device". null = not
  // looked up yet, so the row stays hidden rather than flashing dead cards.
  const [cachedRefs, setCachedRefs] = useState<Set<string> | null>(null);

  useEffect(() => {
    usage.me().then(setU).catch(() => {});
    sectionsApi
      .list()
      .then(setAll)
      .catch(() => setAll([]));
    listSeparations()
      .then((s) => setCachedRefs(new Set(s.map((x) => x.sourceRef).filter(Boolean))))
      .catch(() => setCachedRefs(new Set()));
  }, []);

  const recent = (all ?? []).slice(0, 4);
  // Distinct recent TRACKS (songs) to resume — one card per source video/file,
  // newest first (the list already arrives newest-first, so the first section
  // seen for a source is its most recent).
  const tracks = (() => {
    if (!cachedRefs) return [];
    const seen = new Map<string, { sec: Section; count: number }>();
    for (const s of all ?? []) {
      // A link source with no cache is still resumable — the Studio re-opens
      // capture for it. An upload with no cache is a dead end, so skip it.
      const isUrl = s.source_kind === 'url' || /^https?:\/\//i.test(s.video_id || '');
      if (!isUrl && !cachedRefs.has(s.video_id)) continue;
      const key = `${s.source_kind}::${s.video_id}`;
      const e = seen.get(key);
      if (e) e.count++;
      else seen.set(key, { sec: s, count: 1 });
    }
    // Cap at 4: with the page's 1200px max-width, 4 fixed cards + the dashed
    // filler complete exactly one row. More cards wrap, and the filler then
    // balloons into a huge empty box on the second row (looked bad live,
    // 2026-07-07). Older songs live behind "All songs".
    return Array.from(seen.values()).slice(0, 4);
  })();
  const savedCount = all?.length ?? null;
  const isPro = user?.plan === 'pro';
  const isLifetime = user?.plan_kind === 'lifetime';
  const planLabel = isLifetime ? 'Lifetime' : isPro ? 'Pro' : 'Free';

  const openStudio = () => navigate('/');
  const isolate = (id: string) => navigate('/', { state: { stem: id } });
  const resume = (s: Section) => navigate('/', { state: { reopen: s } });
  // Resume a whole track (YouTube song → re-open in the capture panel; upload →
  // restore its stems). Studio reads this on mount.
  const resumeTrack = (s: Section) => navigate('/', { state: { openTrack: s } });

  return (
    <div className="dash-root">
      <main className="dash-main">
        {/* hero */}
        <section className="dash-hero">
          {/* quick start */}
          <div className="dash-card" style={{ padding: 24 }}>
            <div className="dash-label" style={{ color: 'var(--dash-accent2)', marginBottom: 9 }}>
              QUICK START
            </div>
            <h2 className="dash-grotesk" style={{ margin: '0 0 20px', fontWeight: 600, fontSize: 25, lineHeight: 1.18, letterSpacing: '-0.02em', color: 'var(--dash-text)' }}>
              What are we working on today?
            </h2>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
              <button className="dash-btn-primary" style={{ padding: '0 22px', height: 50, fontSize: 15.5 }} onClick={openStudio}>
                Open Studio <Ar />
              </button>
              <DropBar
                onFile={(f) => {
                  setPendingUpload(f);
                  navigate('/');
                }}
                onClick={openStudio}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--dash-text3)', marginRight: 3 }}>ISOLATE</span>
              {STEM_UI.map((st) => (
                <button key={st.id} className="dash-chip" onClick={() => isolate(st.id)} style={{ ['--chip']: st.color } as React.CSSProperties}>
                  {chipIcon(st.id as StemName)} {st.name}
                </button>
              ))}
            </div>
          </div>

          {/* Riffloop for Chrome — extension promo */}
          <div className="dash-card" style={{ padding: 22, display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--dash-text3)' }}>Riffloop for Chrome</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'color-mix(in srgb, var(--dash-accent) 16%, transparent)', color: 'var(--dash-accent)', padding: '4px 9px', borderRadius: 999, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em' }}>NEW</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
              <span style={{ width: 46, height: 46, flex: 'none', borderRadius: 12, background: '#fff', border: '1px solid var(--dash-border2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><rect x="3" y="5" width="18" height="14" rx="3.5" fill="#FF0000" /><path d="M10.2 9.2v5.6l4.6-2.8z" fill="#fff" /></svg>
              </span>
              <h3 style={{ margin: 0, fontWeight: 700, fontSize: 20, lineHeight: 1.18, letterSpacing: '-0.02em', color: 'var(--dash-text)' }}>Split any track right on YouTube.</h3>
            </div>
            <p style={{ margin: '0 0 16px', fontSize: 13.5, lineHeight: 1.5, color: 'var(--dash-text2)' }}>Add Riffloop to any video — isolate vocals, drums or bass without leaving the page.</p>
            <div style={{ marginTop: 'auto' }}>
              <a
                href={CHROME_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ width: '100%', height: 46, borderRadius: 12, background: 'var(--dash-grad)', color: '#fff', fontWeight: 700, fontSize: 14.5, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 9, textDecoration: 'none', boxShadow: '0 10px 22px -10px var(--dash-accent)' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9.2" fill="#fff" /><path d="M12 2.8a9.2 9.2 0 0 1 7.96 4.6H12a4.6 4.6 0 0 0-4 2.3z" fill="#EA4335" /><path d="M4.04 7.4A9.2 9.2 0 0 0 8.6 19.9l3.98-6.9a4.6 4.6 0 0 1-4.58-2.3z" fill="#34A853" /><path d="M19.96 7.4a9.2 9.2 0 0 1-7.4 13.78l3.98-6.9a4.6 4.6 0 0 0 .02-4.58z" fill="#FBBC05" /><circle cx="12" cy="12" r="3.4" fill="#4285F4" /></svg>
                Add to Chrome — free
              </a>
            </div>
          </div>
        </section>

        {/* continue where you left off — recent SONGS (tracks) with thumbnails */}
        {tracks.length > 0 && (
          <section className="dash-continue">
            <div className="dash-recents-head" style={{ padding: '0 2px', marginBottom: 12 }}>
              <span className="dash-label">CONTINUE WHERE YOU LEFT OFF</span>
              <span className="dash-stat-link" onClick={() => navigate('/sections')}>
                All songs <Ar small />
              </span>
            </div>
            <div className="dash-cont-row">
              {tracks.map(({ sec, count }) => {
                const isUrl = sec.source_kind === 'url' || /^https?:\/\//i.test(sec.video_id || '');
                const isSc = /(^|\.)soundcloud\.com\//i.test(sec.video_id || '');
                const ytId = isUrl && !isSc ? ytIdOf(sec.video_id || '') : null;
                return (
                  <button
                    key={sec.id}
                    className="dash-cont-card"
                    onClick={() => resumeTrack(sec)}
                    title={isUrl ? 'Open this video again' : 'Resume this track'}
                  >
                    <div className="dash-cont-thumb">
                      <span className="dash-cont-ph">{chipIcon('other', 26)}</span>
                      {ytId && (
                        <img
                          src={`https://i.ytimg.com/vi/${ytId}/mqdefault.jpg`}
                          alt=""
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      )}
                      <span className="dash-cont-play">
                        <PlayMini />
                      </span>
                    </div>
                    <div className="dash-cont-meta">
                      <b>{trackTitle(sec)}</b>
                      <span>
                        {isUrl ? (isSc ? 'SoundCloud' : 'YouTube') : 'Upload'} · {count} section{count === 1 ? '' : 's'}
                      </span>
                    </div>
                  </button>
                );
              })}
              {/* Filler CTA — stretches to absorb whatever row width the song
                  cards don't use, so the row always terminates purposefully. */}
              <button className="dash-cont-new" onClick={openStudio} title="Open the Studio">
                <span className="dash-cont-new-ic" aria-hidden="true">+</span>
                <b>Start a new song</b>
                <span className="dash-cont-new-sub">Upload a file or paste a YouTube link</span>
              </button>
            </div>
          </section>
        )}

        {/* lower */}
        <div className={'dash-lower' + (isPro ? ' full' : '')}>
          <section className="dash-card dash-recents">
            <div className="dash-recents-head">
              <span className="dash-label">RECENT ISOLATIONS</span>
              <span className="dash-stat-link" onClick={() => navigate('/sections')}>
                View all <Ar small />
              </span>
            </div>
            {all == null ? (
              <div style={{ padding: '24px 0', textAlign: 'center' }}>
                <div className="rl-spinner" style={{ margin: '0 auto' }} />
              </div>
            ) : recent.length === 0 ? (
              <div style={{ padding: '28px 8px', color: 'var(--dash-text3)', fontSize: 13.5, textAlign: 'center' }}>
                No isolations yet —{' '}
                <span style={{ color: 'var(--dash-accent2)', fontWeight: 700, cursor: 'pointer' }} onClick={openStudio}>
                  open the Studio
                </span>{' '}
                to make your first one.
              </div>
            ) : (
              recent.map((s) => {
                const c = colorOf(s);
                return (
                  <button key={s.id} className="dash-rec" onClick={() => resume(s)}>
                    <div className="dash-rec-ic" style={{ background: tintOf(c), color: c }}>{chipIcon(audibleStem(s), 19)}</div>
                    <div className="dash-rec-meta">
                      <b>{s.label && s.label.trim() ? s.label : mixLabel(s)}</b>
                      <span>
                        {mixLabel(s)} · {fmtTime(s.start)}–{fmtTime(s.end)}
                      </span>
                    </div>
                    <div className="dash-rec-wave">
                      <Wave seed={hashSeed(s.id)} color={c} n={44} height={30} />
                    </div>
                    <span className="dash-rec-play" aria-label="Open in Studio">
                      <I.play />
                    </span>
                    <div className="dash-rec-when">{fmtRelativeDate(s.updated_at)}</div>
                  </button>
                );
              })
            )}
          </section>

          {!isPro && (
            <section className="dash-upsell">
              <div className="blob" style={{ right: -40, top: -40, width: 160, height: 160 }} />
              <div className="blob" style={{ right: 24, bottom: -30, width: 90, height: 90, background: 'rgba(255,255,255,0.1)' }} />
              <div style={{ position: 'relative', zIndex: 1, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'rgba(255,255,255,0.18)', padding: '6px 12px', borderRadius: 999, fontSize: 11.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 15, alignSelf: 'flex-start' }}>
                  <I.bolt /> Riffloop Pro
                </div>
                <h3 className="dash-grotesk" style={{ margin: '0 0 6px', fontWeight: 700, fontSize: 23, lineHeight: 1.15, letterSpacing: '-0.02em' }}>
                  Unlock every stem, every track.
                </h3>
                <p style={{ margin: '0 0 18px', fontSize: 13.5, lineHeight: 1.5, color: 'rgba(255,255,255,0.82)' }}>No limits. Cancel anytime.</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 11, marginBottom: 20 }}>
                  {['Unlimited separations', 'Higher-quality stems on demand', 'Export stems from your own uploads', 'Longer clips, no daily cap'].map((p) => (
                    <div key={p} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5, fontWeight: 500 }}>
                      <span style={{ width: 20, height: 20, borderRadius: '50%', background: 'rgba(255,255,255,0.22)', display: 'grid', placeItems: 'center', flex: 'none' }}>
                        <I.check />
                      </span>
                      {p}
                    </div>
                  ))}
                </div>
                <button style={{ width: '100%', background: '#fff', color: '#5b3ae0', border: 'none', borderRadius: 13, height: 48, fontFamily: 'inherit', fontWeight: 700, fontSize: 15, cursor: 'pointer', marginTop: 'auto' }} onClick={() => navigate('/pricing')}>
                  Upgrade to Pro
                </button>
              </div>
            </section>
          )}
        </div>

        {/* stats — glanceable meta, demoted below the content rows (owner's
            call 2026-07-07: content grouped together, stats at the bottom). */}
        <div className="dash-stats">
          <Stat label="THIS MONTH" icon={<I.bolt />}>
            <div className="dash-stat-num">{u ? u.this_month : <Skel w={48} />}</div>
            <div className="dash-stat-sub">separations</div>
          </Stat>
          <Stat label="LOOPS LEFT" icon={<I.clock />}>
            <div className="dash-stat-num">{u ? (u.daily_quota == null ? '∞' : (u.remaining != null ? u.remaining : Math.max(0, u.daily_quota - u.today))) : <Skel w={48} />}</div>
            <div className="dash-stat-sub">{u && u.daily_quota != null ? `of ${u.daily_quota} left` : 'unlimited'}</div>
          </Stat>
          <Stat label="CURRENT PLAN" icon={<I.star />}>
            <div className="dash-stat-num">{planLabel}</div>
            {isPro ? (
              <div className="dash-stat-sub" style={{ color: 'var(--dash-good)', fontWeight: 700 }}>{isLifetime ? 'Forever' : 'Active'}</div>
            ) : (
              <span className="dash-stat-link" onClick={() => navigate('/pricing')}>
                Upgrade <Ar small />
              </span>
            )}
          </Stat>
          <Stat label="SAVED SECTIONS" icon={<I.folder />}>
            <div className="dash-stat-num">{savedCount != null ? savedCount : <Skel w={36} />}</div>
            <span className="dash-stat-link" onClick={() => navigate('/sections')}>
              See all <Ar small />
            </span>
          </Stat>
        </div>

        <Footer />
      </main>
    </div>
  );
}

function DropBar({ onFile, onClick }: { onFile: (f: File) => void; onClick: () => void }) {
  const [over, setOver] = useState(false);
  return (
    <div
      className={'dash-drop' + (over ? ' over' : '')}
      onClick={onClick}
      onDragOver={(e) => {
        e.preventDefault();
        if (!over) setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) onFile(f);
        else onClick();
      }}
    >
      <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="var(--dash-accent2)" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none' }}>
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <path d="m17 8-5-5-5 5" />
        <path d="M12 3v12" />
      </svg>
      <span style={{ fontSize: 13.5, color: 'var(--dash-text2)', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>…or drop a file to start</span>
    </div>
  );
}

function Stat({ label, icon, children }: { label: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="dash-stat">
      <div className="dash-stat-head">
        <span>{label}</span>
        <span className="dash-stat-ic">{icon}</span>
      </div>
      {children}
    </div>
  );
}

function Skel({ w }: { w: number }) {
  return <span className="dash-skel" style={{ width: w, verticalAlign: 'middle' }} />;
}

// ── icons ──
function Ar({ small }: { small?: boolean } = {}) {
  const s = small ? 13 : 18;
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={small ? 2.4 : 2.3} strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </svg>
  );
}
function PlayMini() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}
// Stem icons matching the original design (mic / disc / wave bars / note),
// inheriting the chip's color via currentColor.
function SV(size: number, children: React.ReactNode) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      {children}
    </svg>
  );
}
function chipIcon(id: StemName, size = 17) {
  switch (id) {
    case 'vocals':
      return SV(size, <>
        <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
        <path d="M12 19v3" />
      </>);
    case 'drums':
      return SV(size, <>
        <path d="M4 8.5c0 1.5 3.6 2.7 8 2.7s8-1.2 8-2.7S16.4 5.8 12 5.8 4 7 4 8.5Z" />
        <path d="M4 8.5v6.4c0 1.5 3.6 2.7 8 2.7s8-1.2 8-2.7V8.5" />
        <path d="M8.2 16.2 6 21M15.8 16.2 18 21" />
      </>);
    case 'bass':
      return SV(size, <>
        <rect x="5" y="2.5" width="14" height="19" rx="2.5" />
        <circle cx="12" cy="14" r="4.6" />
        <circle cx="12" cy="14" r="1.5" />
        <circle cx="12" cy="6" r="0.9" />
      </>);
    case 'piano':
      return SV(size, <>
        <rect x="3" y="5" width="18" height="14" rx="1.5" />
        <path d="M8 5v14M12 5v14M16 5v14" />
      </>);
    case 'guitar':
      return SV(size, <>
        <path d="M11.5 8.5 19 1l4 4-7.5 7.5" />
        <circle cx="8" cy="15" r="5" />
        <circle cx="8" cy="15" r="1.6" />
      </>);
    default:
      return SV(size, <>
        <path d="M9 18V5l11-2v13" />
        <circle cx="6" cy="18" r="3" />
        <circle cx="18" cy="16" r="3" />
      </>);
  }
}

const I = {
  bolt: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
      <path d="M13 2 4.5 13.5H11l-1 8 8.5-11.5H12z" />
    </svg>
  ),
  clock: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  ),
  star: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 3l1.7 4.8L18.5 9l-4.8 1.7L12 15l-1.7-4.3L5.5 9l4.8-1.2L12 3z" />
    </svg>
  ),
  folder: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
    </svg>
  ),
  play: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
      <path d="M8 5v14l11-7z" />
    </svg>
  ),
  check: () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  ),
  note: () => (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 18V5l11-2v13" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="18" cy="16" r="3" />
    </svg>
  ),
};
